import { Chip } from '@liveauctioneers/hammer-ui-core/chips';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { CurrentOpenMenu, FacetChipOption } from '@/pages/SearchPageSimplified/types';
import { getFilterActionByFacetId } from '@/pages/Search/KeywordSearch/utils/getFilterActionByFacetId';
import { keywordFilterChipsAtom, visibleFilterChipsAtom } from '@/pages/SearchPageSimplified/atoms/searchPageAtoms';
import {
    modifyParamsRemovePage,
    modifyParamsRemoveParamByFacetId,
    modifyParamsWhenChecked,
    modifyParamsWithFilterTerm,
    modifyParamsWithRange,
    parseQueryParams,
} from '@/utils/queryParams';
import { useAtomValue } from 'jotai';
import { useLocation } from 'react-router-dom';
import { useNavigateSearchPageWithParams } from '@/pages/SearchPageSimplified/hooks/useNavigateSearchPageWithParams';
import FacetTypes from '@/types/search/enums/facetTypes';

type Props = {
    className?: string;
    isAllFilters?: boolean;
};

export const SearchFilterChips = ({ className, isAllFilters }: Props) => {
    const { search } = useLocation();
    const queryParams = parseQueryParams(search);

    const navigateWithParams = useNavigateSearchPageWithParams();

    const keywordFilterChips = useAtomValue(keywordFilterChipsAtom);
    const visibleFilterChips = useAtomValue(visibleFilterChipsAtom);

    const onRemoveFilterChip = ({ id, label, value }: FacetChipOption) => {
        let newParams;

        switch (value) {
            case FacetTypes.CURRENT_BID_ID:
                newParams = modifyParamsWithRange(queryParams, FacetTypes.CURRENT_BID_ID, 0, 0);
                newParams = modifyParamsRemoveParamByFacetId(newParams, FacetTypes.CURRENT_BID_ID);
                break;
            case FacetTypes.ESTIMATED_PRICE_ID:
                newParams = modifyParamsWithRange(queryParams, FacetTypes.ESTIMATED_PRICE_ID, 0, 0);
                newParams = modifyParamsRemoveParamByFacetId(newParams, FacetTypes.ESTIMATED_PRICE_ID);
                break;
            case FacetTypes.HAMMER_PRICE_ID:
                newParams = modifyParamsWithRange(queryParams, FacetTypes.HAMMER_PRICE_ID, 0, 0);
                newParams = modifyParamsRemoveParamByFacetId(newParams, FacetTypes.HAMMER_PRICE_ID);
                break;
            case FacetTypes.KEYWORD_ID:
                newParams = modifyParamsWithFilterTerm(queryParams, label);
                break;
            case FacetTypes.ORIGIN_ID:
                newParams = modifyParamsWhenChecked(queryParams, FacetTypes.ORIGIN_ID, id, false);
                break;

            // TREAT AS OPTIONS
            case FacetTypes.SALE_TYPE_ID:
                newParams = modifyParamsWhenChecked(queryParams, FacetTypes.SALE_TYPE_ID, id, false);
                break;
            case FacetTypes.AUCTION_HOUSE_ID:
                newParams = modifyParamsWhenChecked(queryParams, FacetTypes.AUCTION_HOUSE_ID, id, false);
                break;
            case FacetTypes.MATERIALS_AND_TECHNIQUES_ID:
                newParams = modifyParamsWhenChecked(queryParams, FacetTypes.MATERIALS_AND_TECHNIQUES_ID, id, false);
                break;
            case FacetTypes.STYLE_PERIOD_ID:
                newParams = modifyParamsWhenChecked(queryParams, FacetTypes.STYLE_PERIOD_ID, id, false);
                break;
            case FacetTypes.CREATOR_ID:
                newParams = modifyParamsWhenChecked(queryParams, FacetTypes.CREATOR_ID, id, false);
                break;
            default:
                break;
        }

        newParams = modifyParamsRemovePage(newParams);

        navigateWithParams({
            checkForPageRemoval: true,
            currentOpenMenu: isAllFilters ? CurrentOpenMenu.AllFilters : undefined,
            filterAction: getFilterActionByFacetId({ facetId: value, isRemovingFilter: true }),
            params: newParams,
            preventScrollReset: true,
        });
    };

    if (visibleFilterChips.length === 0) {
        return null;
    }

    return (
        <div className={cn('mb-md flex flex-wrap gap-xs', { '!mb-0 mt-xs': isAllFilters }, className)}>
            {isAllFilters
                ? keywordFilterChips.map((chip) => (
                      <Chip
                          aria-label={`Remove ${chip.label.trim()} filter`}
                          id={`filter-${chip.label}`}
                          key={chip.label}
                          onClose={() => onRemoveFilterChip(chip)}
                          selected
                          style="input"
                      >
                          "{chip.label}"
                      </Chip>
                  ))
                : visibleFilterChips.map((chip) => (
                      <Chip
                          aria-label={`Remove ${chip.label.trim()} filter`}
                          id={`filter-${chip.label}`}
                          key={chip.label}
                          onClose={() => onRemoveFilterChip(chip)}
                          selected
                          style="input"
                      >
                          "{chip.label}"
                      </Chip>
                  ))}
        </div>
    );
};
